import { boundMethod } from 'autobind-decorator';
import type {
  AsyncStatus,
  WidgetFlowT,
  WidgetT,
  WidgetUpdate,
} from 'lib-shared-legacy-types';
import _isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

import { ajaxSend } from 'api/api';
import { omitNullValues } from 'utils/object';
import { NullableValues } from 'utils/typeUtils';

import type {
  WidgetCreatePayloadT,
  WidgetsCreateResponseT,
  WidgetsGetItemResponseT,
  WidgetsGetItemsResponseT,
  WidgetsQueryParams,
  WidgetsRemoveResponseT,
  WidgetsUpdateResponseT,
} from './api/types';
import { widgetsApi } from './api/widgets';

const { createWidget, getWidget, getWidgets, removeWidget, updateWidget } =
  widgetsApi;

const DEFAULT_FETCH_THROTTLE = 300;

export interface WithWidgetsInjectedPropsT {
  widgets: {
    /**
     * Create new widget
     *
     * @param {WidgetCreatePayloadT} data - Widget to be created
     */
    create: (data: WidgetCreatePayloadT) => Promise<WidgetsCreateResponseT>;

    /** AsyncStatus of create widget api */
    createAsyncStatus: AsyncStatus;

    /**
     * Get a single widget
     *
     * @param {string} widgetId - Id of the widget to be retrieved.
     */
    getItem: (widgetId: string) => Promise<WidgetsGetItemResponseT>;

    /** AsyncStatus of get single widget item api */
    getItemAsyncStatus: AsyncStatus;

    /** Action to get widget items of the current page */
    getItems: () => Promise<WidgetsGetItemsResponseT>;

    /** AsyncStatus of get widget items list api */
    getItemsAsyncStatus: AsyncStatus;

    /** Whether there is next page of widgets or not */
    hasNextPage: boolean;

    /** Whether there is previous page of widgets or not */
    hasPreviousPage: boolean;

    /** Loaded single widget item */
    item: null | WidgetT;

    /** Loaded widget items of current page */
    items: WidgetFlowT[];

    /** Get widget items of the next page */
    nextPage: () => Promise<WidgetsGetItemsResponseT>;

    /** The params used in get widgets api */
    params: WidgetsQueryParams;

    /**
     * Handle params change.
     *
     * @param {WidgetsQueryParams} params - The params
     * @param {number} [fetchThrottle=300] - If > 0 it will re-fetch widgets
     *   after given milliseconds. Default is `300`
     */
    paramsChange: (
      params: NullableValues<WidgetsQueryParams>,
      fetchThrottle?: number,
    ) => Promise<WidgetsGetItemsResponseT | void>;

    /** Get widget items of the previous page */
    previousPage: () => Promise<WidgetsGetItemsResponseT>;

    /**
     * Remove widget
     *
     * @param {string} widgetId - Id of the widget to be removed.
     */
    remove: (widgetId: string) => Promise<WidgetsRemoveResponseT>;

    /** AsyncStatus of remove widget api */
    removeAsyncStatus: AsyncStatus;

    /**
     * Remove widget
     *
     * @param {string} widgetId - Id of the widget to be updated
     * @param {WidgetUpdate} payload - Payload used in update widgets api
     */
    update: (
      widgetId: string,
      payload: WidgetUpdate,
    ) => Promise<WidgetsUpdateResponseT>;

    /** AsyncStatus of update widget api */
    updateAsyncStatus: AsyncStatus;
  };
}

type WithWidgetsNotInjectedProps<T> = Omit<T, keyof WithWidgetsInjectedPropsT>;
type WithWidgetsPropsT<T> = WithWidgetsNotInjectedProps<T>;

interface WithWidgetsStateT {
  item: null | WidgetT;
  items: WidgetFlowT[];
  params: WidgetsQueryParams;
  nextPagePath: null | string;
  previousPagePath: null | string;
  createAsyncStatus: AsyncStatus;
  getItemAsyncStatus: AsyncStatus;
  getItemsAsyncStatus: AsyncStatus;
  removeAsyncStatus: AsyncStatus;
  updateAsyncStatus: AsyncStatus;
}

interface WithWidgetsSettings {
  initialParams?: WidgetsQueryParams;
}

export const withWidgets = <T extends WithWidgetsInjectedPropsT>(
  Component: ComponentType<T>,
  settings: WithWidgetsSettings = {},
): ComponentType<WithWidgetsPropsT<T>> => {
  class WithWidgets extends PureComponent<
    WithWidgetsPropsT<T>,
    WithWidgetsStateT
  > {
    timeout: ReturnType<typeof setTimeout> | null;

    constructor(props: WithWidgetsPropsT<T>) {
      super(props);

      this.timeout = null;
      this.state = {
        item: null,
        items: [],
        params: { limit: 20, ...settings.initialParams },
        nextPagePath: null,
        previousPagePath: null,
        createAsyncStatus: 'succeeded',
        getItemAsyncStatus: 'succeeded',
        getItemsAsyncStatus: 'succeeded',
        removeAsyncStatus: 'succeeded',
        updateAsyncStatus: 'succeeded',
      };

      /** We have to bind it like this. Using @boundMethod won't work */
      this.memoizedGetInjectedProps = memoize(
        this.memoizedGetInjectedProps,
        _isEqual,
      ).bind(this);
    }

    componentWillUnmount() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
    }

    @boundMethod
    getItem(widgetId: string): Promise<WidgetsGetItemResponseT> {
      this.setState({ getItemAsyncStatus: 'loading' });

      return getWidget(widgetId)
        .then((response) => {
          this.setState({
            getItemAsyncStatus: 'succeeded',
            item: response.data,
          });

          return response;
        })
        .catch((error: Error) => {
          this.setState({ item: null, getItemAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    getItems(): Promise<WidgetsGetItemsResponseT> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      const params = { ...this.state.params };

      if (params?.search) {
        delete params.sort_by; // We do not support sort and search in the same query
      }

      return getWidgets(params)
        .then(this.handleQueryResolve)
        .catch(this.handleQueryReject);
    }

    @boundMethod
    getItemsWithPath(path: string): Promise<WidgetsGetItemsResponseT> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      return ajaxSend<WidgetsGetItemsResponseT>({ path })
        .then(this.handleQueryResolve)
        .catch(this.handleQueryReject);
    }

    @boundMethod
    nextPage(): Promise<WidgetsGetItemsResponseT> {
      const { nextPagePath } = this.state;

      if (nextPagePath) {
        return this.getItemsWithPath(nextPagePath);
      }
      throw new Error('Next page does not exist');
    }

    @boundMethod
    previousPage(): Promise<WidgetsGetItemsResponseT> {
      const { previousPagePath } = this.state;

      if (previousPagePath) {
        return this.getItemsWithPath(previousPagePath);
      }
      throw new Error('Previous page does not exist');
    }

    @boundMethod
    create(data: WidgetCreatePayloadT): Promise<WidgetsCreateResponseT> {
      this.setState({ createAsyncStatus: 'loading' });

      return createWidget(data)
        .then((response) => {
          this.setState({ createAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((error: Error) => {
          this.setState({ createAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    update(
      widgetId: string,
      payload: WidgetUpdate,
    ): Promise<WidgetsUpdateResponseT> {
      this.setState({ updateAsyncStatus: 'loading' });

      return updateWidget(widgetId, payload)
        .then((response) => {
          this.setState({ updateAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((error: Error) => {
          this.setState({ updateAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    remove(widgetId: string): Promise<WidgetsRemoveResponseT> {
      this.setState({ removeAsyncStatus: 'loading' });

      return removeWidget(widgetId)
        .then((response) => {
          this.setState({ removeAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((error: Error) => {
          this.setState({ removeAsyncStatus: 'failed' });
          throw error;
        });
    }

    @boundMethod
    handleQueryResolve(
      response: WidgetsGetItemsResponseT,
    ): Promise<WidgetsGetItemsResponseT> {
      return new Promise(() => {
        this.setState({
          items: response.data.widgets,
          getItemsAsyncStatus: 'succeeded',
          nextPagePath: response.meta.next_page,
          previousPagePath: response.meta.prev_page,
        });
      });
    }

    @boundMethod
    handleQueryReject(error) {
      this.setState({
        getItemsAsyncStatus: 'failed',
      });

      return error;
    }

    @boundMethod
    handleParamsChange(
      params?: NullableValues<WidgetsQueryParams>,
      fetchThrottle: number = DEFAULT_FETCH_THROTTLE,
    ) {
      return new Promise<WidgetsGetItemsResponseT | void>((resolve) => {
        this.setState(
          (state) => ({
            // remove values that are `nulled` by the new param
            params: omitNullValues({
              ...state.params,
              ...params,
            }) as WidgetsQueryParams,
          }),
          () => {
            if (fetchThrottle >= 0) {
              if (this.timeout) {
                clearTimeout(this.timeout);
              }

              this.timeout = setTimeout(() => {
                resolve(this.getItems());
              }, fetchThrottle);
            } else {
              resolve();
            }
          },
        );
      });
    }

    @boundMethod
    getInjectedProps(
      _props: Readonly<WithWidgetsPropsT<T>>,
      state: Readonly<WithWidgetsStateT>,
    ): WithWidgetsInjectedPropsT['widgets'] {
      const {
        createAsyncStatus,
        getItemAsyncStatus,
        getItemsAsyncStatus,
        item,
        items,
        nextPagePath,
        params,
        previousPagePath,
        removeAsyncStatus,
        updateAsyncStatus,
      } = state;

      return this.memoizedGetInjectedProps(
        item,
        items,
        params,
        createAsyncStatus,
        getItemAsyncStatus,
        getItemsAsyncStatus,
        nextPagePath,
        previousPagePath,
        removeAsyncStatus,
        updateAsyncStatus,
      );
    }

    memoizedGetInjectedProps(
      item: Readonly<WithWidgetsStateT>['item'],
      items: Readonly<WithWidgetsStateT>['items'],
      params: Readonly<WithWidgetsStateT>['params'],
      createAsyncStatus: Readonly<WithWidgetsStateT>['createAsyncStatus'],
      getItemAsyncStatus: Readonly<WithWidgetsStateT>['getItemAsyncStatus'],
      getItemsAsyncStatus: Readonly<WithWidgetsStateT>['getItemsAsyncStatus'],
      nextPagePath: Readonly<WithWidgetsStateT>['nextPagePath'],
      previousPagePath: Readonly<WithWidgetsStateT>['previousPagePath'],
      removeAsyncStatus: Readonly<WithWidgetsStateT>['removeAsyncStatus'],
      updateAsyncStatus: Readonly<WithWidgetsStateT>['updateAsyncStatus'],
    ): WithWidgetsInjectedPropsT['widgets'] {
      return {
        item,
        items,
        params,
        createAsyncStatus,
        getItemAsyncStatus,
        getItemsAsyncStatus,
        removeAsyncStatus,
        updateAsyncStatus,
        hasNextPage: nextPagePath !== null,
        hasPreviousPage: previousPagePath !== null,
        create: this.create,
        getItem: this.getItem,
        getItems: this.getItems,
        nextPage: this.nextPage,
        paramsChange: this.handleParamsChange,
        previousPage: this.previousPage,
        remove: this.remove,
        update: this.update,
      };
    }

    render() {
      return (
        <Component
          {...(this.props as T)}
          widgets={this.getInjectedProps(this.props, this.state)}
        />
      );
    }
  }

  return WithWidgets;
};
